import { enUS } from 'date-fns/locale';
import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './CustomDatePicker.css';
import { formatDateToString } from '../utils/formatDate';

type CustomDatePickerProps = {
  value?: string;
  onChange: (value: string) => void;
  label?: string;
  min?: string;
  max?: string;
  placeholder?: string;
  name?: string;
  showTimeSelect?: boolean;
  timeIntervals?: number;
  dateFormat?: string;
};

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h1V3a1 1 0 0 1 1-1Zm13 8H4v9a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-9ZM5 6a1 1 0 0 0-1 1v1h16V7a1 1 0 0 0-1-1H5Z" />
  </svg>
);

const parseDate = (value?: string): Date | null => {
  if (!value) return null;

  // with time: "2026-04-08T14:30" or "2026-04-08T00:00"
  if (value.includes('T')) {
    const [datePart, timePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);

    if (!year || !month || !day) return null;

    return new Date(year, month - 1, day, hours || 0, minutes || 0);
  }

  // date only: "2026-04-08"
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};
const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  label,
  min,
  max,
  placeholder,
  name,
  showTimeSelect,
  timeIntervals = 5,
  dateFormat,
}) => {
  const selectedDate = parseDate(value);
  const minDate = parseDate(min);
  const maxDate = parseDate(max);

  return (
    <div className="custom-date-picker">
      {label && <label className="input-label">{label}</label>}

      <div className="input-wrapper">
        <DatePicker
          name={name}
          selected={selectedDate}
          onChange={(date: Date | null) => onChange(formatDateToString(date, showTimeSelect))}
          minDate={minDate}
          maxDate={maxDate}
          dateFormat={dateFormat || (showTimeSelect ? 'MM/dd/yyyy HH:mm' : 'MM/dd/yyyy')}
          locale={enUS}
          placeholderText={placeholder || (showTimeSelect ? 'MM/dd/yyyy HH:mm' : 'MM/dd/yyyy')}
          className="custom-input"
          calendarClassName="custom-date-calendar"
          popperClassName="custom-date-popper"
          showPopperArrow={false}
          showMonthDropdown
          showYearDropdown
          scrollableYearDropdown
          dropdownMode="select"
          showTimeSelect={showTimeSelect}
          timeFormat="HH:mm"
          timeIntervals={timeIntervals}
          autoComplete="off"
        />

        <span className="input-icon">
          <CalendarIcon />
        </span>
      </div>
    </div>
  );
};

export default CustomDatePicker;
